import { useAuth0 } from "@auth0/auth0-react";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";

export function CallbackPage() {
  const { isLoading, error, isAuthenticated, loginWithRedirect, logout } = useAuth0();
  const navigate = useNavigate();

  // Once Auth0 finishes the callback exchange and the user is signed in,
  // hop to the chat surface. (Auth0Provider's onRedirectCallback also routes
  // home — this is a safety net for refreshes.)
  useEffect(() => {
    if (!isLoading && !error && isAuthenticated) {
      navigate("/chat", { replace: true });
    }
  }, [isLoading, error, isAuthenticated, navigate]);

  const retry = () => {
    // Wipe any stale Auth0 cache before restarting the flow — the most common
    // cause of "Invalid state" is a leftover transaction from a previous
    // session.
    try {
      Object.keys(localStorage)
        .filter((k) => k.startsWith("@@auth0spajs@@"))
        .forEach((k) => localStorage.removeItem(k));
    } catch {
      /* ignore storage errors */
    }
    loginWithRedirect({
      authorizationParams: { prompt: "login" },
      appState: { returnTo: "/chat" },
    });
  };

  if (error) {
    return (
      <div className="mx-auto mt-24 max-w-reading px-6 text-center">
        <span className="smallcaps text-oxblood-500">Sign-in error</span>
        <h2 className="mt-2 font-serif text-headline text-espresso-700">{error.message}</h2>
        <p className="mx-auto mt-4 max-w-md font-serif text-dek text-warmgray-500">
          This usually means a previous login attempt left stale state in your browser, or you
          opened the login in another tab. Click below to clear it and start over.
        </p>
        <div className="mt-8 flex justify-center gap-3">
          <button
            type="button"
            onClick={retry}
            className="border border-oxblood-500 bg-oxblood-500 px-6 py-2 font-serif text-base text-parchment-50 transition-colors hover:bg-oxblood-600"
          >
            Try again
          </button>
          <button
            type="button"
            onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}
            className="border border-ruleline px-6 py-2 font-serif text-base text-warmgray-500 transition-colors hover:border-oxblood-300 hover:text-oxblood-500"
          >
            Sign out everywhere
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen items-center justify-center text-warmgray-400">
      <span className="smallcaps">Signing you in</span>
    </div>
  );
}
